import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  CalendarDays, Users, CreditCard, ArrowRight, Star, 
  CheckCircle2, Dumbbell, Sparkles, ChevronRight, Search,
  Building2, Clock, MapPin
} from 'lucide-react';

const features = [
  {
    icon: CalendarDays,
    title: "Smart Scheduling",
    description: "Effortlessly manage classes, appointments, and instructor schedules with our intuitive calendar system."
  },
  {
    icon: Users,
    title: "Member Management",
    description: "Track memberships, monitor attendance, and build lasting relationships with your clients."
  },
  {
    icon: CreditCard,
    title: "Seamless Payments",
    description: "Process payments securely with RyftPay integration. Automated billing and invoicing included."
  },
  {
    icon: Building2,
    title: "Multi-Location Support",
    description: "Manage multiple studios from one dashboard. Perfect for growing fitness businesses."
  }
];

const testimonials = [
  {
    name: "Sarah Chen",
    role: "Yoga Studio Owner",
    content: "StudioScheduler transformed how I run my business. Booking management is now effortless!",
    avatar: "https://images.unsplash.com/photo-1669504243706-1df1f8d5dacd?w=100&h=100&fit=crop"
  },
  {
    name: "Mike Thompson",
    role: "CrossFit Box Owner",
    content: "The scheduling features save me hours every week. My members love the easy booking system.",
    avatar: "https://images.unsplash.com/photo-1750698545009-679820502908?w=100&h=100&fit=crop"
  }
];

export const LandingPage = () => {
  const { isAuthenticated, user } = useAuth();
  const [studios, setStudios] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchStudios();
  }, []);

  const fetchStudios = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/studios`);
      const data = await response.json();
      setStudios(data.slice(0, 3));
    } catch (e) {
      console.error('Failed to fetch studios:', e);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      {/* Bug R4 companion: `min-h-[90vh]` in Safari mobile collapses when
          the URL bar hides, sometimes overlapping the footer below. Use
          dvh where available so the hero tracks the actual usable
          viewport. Tailwind emits the raw arbitrary value. */}
      <section className="relative min-h-[90vh] min-h-[90dvh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1761971975962-9cc397e2ba2a?w=1920&q=80)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <Badge className="mb-6 bg-primary/20 text-primary border-primary/30 hover:bg-primary/30">
              <Sparkles className="w-3 h-3 mr-1" />
              Now with RyftPay Integration
            </Badge>
            
            <h1 className="text-5xl md:text-7xl font-extrabold text-white leading-tight tracking-tighter mb-6">
              Run Your Studio,
              <span className="block text-primary">Not Your Admin</span>
            </h1>
            
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              The all-in-one platform for fitness studios. Manage scheduling, memberships, 
              payments, and communications — all from one beautiful dashboard.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              {isAuthenticated ? (
                // Bug #9 fix: for logged-in CLIENTS, promote "Browse Studios"
                // as the primary CTA so booking is one click away. Studio
                // owners still get "Go to Dashboard" as the primary action
                // because their journey is managing the studio, not booking.
                user?.role === 'client' ? (
                  <>
                    <Link to="/studios">
                      <Button size="lg" className="btn-lift shadow-lg shadow-primary/30 text-lg h-14 px-8" data-testid="hero-browse-studios-btn">
                        Browse Studios
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                    <Link to="/dashboard">
                      <Button size="lg" variant="outline" className="text-lg h-14 px-8 border-white/20 text-white hover:bg-white/10" data-testid="hero-dashboard-btn">
                        My Dashboard
                      </Button>
                    </Link>
                  </>
                ) : (
                  <Link to="/dashboard">
                    <Button size="lg" className="btn-lift shadow-lg shadow-primary/30 text-lg h-14 px-8" data-testid="hero-dashboard-btn">
                      Go to Dashboard
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                )
              ) : (
                <>
                  <Link to="/register">
                    <Button size="lg" className="btn-lift shadow-lg shadow-primary/30 text-lg h-14 px-8" data-testid="hero-get-started-btn">
                      Get Started Free
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                  <Link to="/studios">
                    <Button size="lg" variant="outline" className="text-lg h-14 px-8 border-white/20 text-white hover:bg-white/10" data-testid="hero-browse-btn">
                      Browse Studios
                    </Button>
                  </Link>
                </>
              )}
            </div>

            <div className="mt-12 flex items-center gap-8 text-slate-400">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span>Free 14-day trial</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span>No credit card required</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">Features</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Everything You Need to
              <span className="text-primary"> Succeed</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Powerful tools designed specifically for fitness studios, yoga centers, and wellness businesses.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-border/50" data-testid={`feature-card-${i}`}>
                <CardHeader>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Studios Preview Section */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <Badge variant="outline" className="mb-4">Discover</Badge>
              <h2 className="text-4xl font-bold mb-2">Featured Studios</h2>
              <p className="text-muted-foreground">Find your perfect workout destination</p>
            </div>
            <Link to="/studios" className="mt-4 md:mt-0">
              <Button variant="outline" className="group" data-testid="view-all-studios-btn">
                View All Studios
                <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          {studios.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {studios.map((studio, i) => (
                <Link key={studio.id} to={`/studio/${studio.slug}`}>
                  <Card className="overflow-hidden group hover:shadow-xl transition-all duration-300" data-testid={`studio-preview-${i}`}>
                    <div className="aspect-video relative overflow-hidden bg-muted">
                      {/* Bug R3 fix (Iter-67): cascade cover → logo → default
                          placeholder, and onError swap so a 404 image URL
                          doesn't leave the card with a broken-image icon.
                          "studio missing thumbnail" was almost always a
                          dead cover_image_url on a legacy row. */}
                      <img
                        src={studio.cover_image_url || studio.logo_url || 'https://images.unsplash.com/photo-1767611114990-6aa3b22cde42?w=600&q=80'}
                        alt={studio.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          if (e.currentTarget.dataset.fallbackApplied) return;
                          e.currentTarget.dataset.fallbackApplied = '1';
                          e.currentTarget.src = 'https://images.unsplash.com/photo-1767611114990-6aa3b22cde42?w=600&q=80';
                        }}
                        data-testid={`studio-preview-image-${i}`}
                      />
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-white/90 text-slate-900">{studio.category || 'Fitness'}</Badge>
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        {studio.name}
                        <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                      </CardTitle>
                      <CardDescription className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {studio.address || 'Location available'}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Dumbbell className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Studios Yet</h3>
              <p className="text-muted-foreground mb-6">Be the first to create a studio on our platform!</p>
              <Link to="/register">
                <Button data-testid="create-first-studio-btn">Create Your Studio</Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">Testimonials</Badge>
            <h2 className="text-4xl font-bold mb-4">Loved by Studio Owners</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((t, i) => (
              <Card key={i} className="p-6" data-testid={`testimonial-${i}`}>
                <div className="flex items-start gap-4">
                  <img 
                    src={t.avatar} 
                    alt={t.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-lg mb-4">"{t.content}"</p>
                    <p className="font-semibold">{t.name}</p>
                    <p className="text-sm text-muted-foreground">{t.role}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Studio?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Join thousands of fitness professionals who trust StudioScheduler.
          </p>
          <Link to="/register">
            <Button size="lg" className="btn-lift shadow-lg shadow-primary/30 text-lg h-14 px-10" data-testid="cta-get-started-btn">
              Start Your Free Trial
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      {/* Bug R4 fix (Iter-67): Safari mobile computed padding differently
          when `vh` unit collapsed on URL-bar hide, letting the footer
          overlap content on smaller displays. Explicit `gap-4` prevents
          items from stacking too tight, `pb-[env(safe-area-inset-bottom)]`
          keeps content clear of the iOS home indicator. */}
      <footer className="bg-slate-950 text-slate-400 py-12 pb-[max(3rem,env(safe-area-inset-bottom))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Dumbbell className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-white">StudioScheduler</span>
            </div>
            <p className="text-sm text-center">© 2026 StudioScheduler. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
